import { FieldModel, ProgramBenchmarkName, ProgramTask, TaskDataTraitStatus } from '@cibo/core'
import { EditSingleFieldFormWithoutModal, useRemoveDetail } from '@cibo/landmanager'
import TrashIcon from '@mui/icons-material/DeleteOutline'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { PROGRAMS_QUERY_KEY } from '../../../../../queries/queryKey'
import { useDataEntrySchemaEditing } from '../../../Tasks/DataEntry/DataEntrySchemaEditingProvider'
import { SingleTargetSchemaEditor } from '../../../Tasks/DataEntry/SingleTargetSchemaEditor'

interface TraitEditorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  anchorEl: HTMLElement | null
  traitId: string
  year?: number
  selectedField?: FieldModel
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
}

export const TraitEditorModal = ({
  isOpen,
  onClose,
  onSuccess,
  anchorEl,
  traitId,
  year,
  selectedField,
  task,
}: TraitEditorModalProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')
  const { saveTaskData } = useDataEntrySchemaEditing()
  const { taskData, program, engagement } = useProgramEngagementContext()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [editorIsDirty, setEditorIsDirty] = useState(false)
  const [unsavedGuardOpen, setUnsavedGuardOpen] = useState(false)
  const removeDetail = useRemoveDetail()
  const handlePopoverClose = () => {
    if (editorIsDirty) {
      setUnsavedGuardOpen(true)
    } else {
      onClose()
    }
  }
  const handleDelete = () => {
    if (!selectedField?.resourceId || !traitId) {
      return
    }

    removeDetail
      .mutateAsync({
        resourceId: selectedField?.resourceId,
        traitId,
        ...(year !== undefined ? { year } : {}),
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, engagement.data?.id],
        })
        queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA] })
        setDeleteConfirmOpen(false)
        onClose()
        enqueueSnackbar(t('deleteSuccess', { trait: t(traitId, { ns: 'traits' }) }), {
          variant: 'success',
        })
        onSuccess && onSuccess()
      })
      .catch(() => {
        setDeleteConfirmOpen(false)
      })
  }

  useEffect(() => {
    if (!isOpen) {
      setEditorIsDirty(false)
    }
  }, [isOpen])

  const traitStatus = taskData.data?.getTraitStatus(traitId)

  const traitConfig =
    task.rule?.params &&
    task.rule.params.traits?.find((trait: { traitId: string }) => trait.traitId === traitId)

  const useSchema = traitConfig?.schema
  const ruleParams = task.rule?.params || {}
  const benchmarkName = ruleParams.benchmarkName
  const benchmarkRequirements = program.data?.benchmarks?.find(
    benchmark => benchmark.benchmark === benchmarkName
  )?.details

  const relevantRequirement =
    benchmarkRequirements &&
    benchmarkRequirements.find(
      (req: any) =>
        req.traitId === traitId &&
        (year === undefined ? req.year === undefined : req.year === year || req.year === undefined)
    )

  const handleSaveTraitDetail = (detail: { value: any; year?: number }) => {
    return (
      selectedField &&
      traitConfig &&
      saveTaskData(
        {
          fieldDetails: [
            {
              resourceId: selectedField.resourceId,
              details: [
                {
                  traitId: traitConfig.traitId,
                  input: detail.value,
                  ...(year !== undefined ? { year } : {}),
                },
              ],
            },
          ],
        },
        {
          onError: error => {
            // @ts-ignore
            enqueueSnackbar(error.response.data.error, { variant: 'error' })
          },
        }
      )
    )
  }

  const traitDetailValue =
    traitStatus &&
    taskData.data?.getDetailValue({
      traitConfig: traitStatus,
      detailRequirement: relevantRequirement,
      resourceId: selectedField?.resourceId,
    })
  if (!selectedField) {
    return null
  }

  if (!useSchema && !relevantRequirement) {
    return null
  }

  const maxPaperHeight = anchorEl
    ? Math.max(window.innerHeight - anchorEl.getBoundingClientRect().bottom, 100)
    : undefined

  const DeleteButton = !!traitId &&
    selectedField?.findDetail({ traitId, ...(year !== undefined ? { year } : {}) }) && (
      <Button
        style={{ width: '100%' }}
        variant="outlined"
        onClick={() => setDeleteConfirmOpen(true)}
        color="error"
        // loading={saveTaskData.isPending}
        data-testid="delete-detail-button"
        startIcon={<TrashIcon />}
      >
        {t('deletePractice')}
      </Button>
    )

  return (
    <>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: { sx: { maxWidth: 300, maxHeight: maxPaperHeight, overflowY: 'auto' } },
        }}
      >
        {useSchema ? (
          <SingleTargetSchemaEditor
            traitStatus={traitStatus}
            handleSaveTraitDetail={value => handleSaveTraitDetail(value)}
            traitDetailValue={traitDetailValue}
            schema={traitConfig.schema}
            year={year}
            variant="compact"
            fieldWrapperProps={{ p: 2 }}
            buttonWrapperProps={{ sx: { background: 'background.paper' }, spacing: 2, p: 2, pt: 0 }}
            submitButtonProps={{
              style: { width: '100%', marginTop: 16 },
              variant: 'contained',
            }}
            DeleteButton={DeleteButton}
            onSuccess={onSuccess}
            onDirtyChange={setEditorIsDirty}
          />
        ) : (
          <EditSingleFieldFormWithoutModal
            traitId={traitId}
            resourceId={selectedField.resourceId}
            requirement={relevantRequirement}
            onSuccess={onSuccess}
            requestClose={onClose}
            DeleteButton={DeleteButton}
            filterInputs={undefined}
            simple
            onDirtyChange={setEditorIsDirty}
          />
        )}
      </Popover>
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirmDeleteMessage', { trait: t(traitId || '', { ns: 'traits' }) })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            loading={removeDetail.isPending}
            data-testid="confirm-delete-button"
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unsavedGuardOpen} onClose={() => setUnsavedGuardOpen(false)}>
        <DialogTitle>{t('unsavedChangesTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('unsavedChangesMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUnsavedGuardOpen(false)
              onClose()
            }}
            color="error"
            data-testid="discard-changes-button"
          >
            {t('deleteData')}
          </Button>
          <Button variant="contained" onClick={() => setUnsavedGuardOpen(false)} color="primary">
            {t('keepEditing')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
